import { useMemo, useState } from "react";
import { SOURCE_MONTHLY_EUR, SOURCE_REQUEST_TURNAROUND } from "@/config/horizonscanning/options";
import { searchSources, publisherKey } from "@/config/horizonscanning/sources";
import type { WizardState } from "@/config/horizonscanning/types";
import { cn } from "@/utils/cn";

interface SourcesStepProps {
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
}

export function SourcesStep({ state, onChange }: SourcesStepProps) {
  const [query, setQuery] = useState("");
  const results = useMemo(() => searchSources(query), [query]);
  const sourceCount = state.publisherIds.length;

  const toggle = (id: string) => {
    const selected = state.publisherIds.includes(id);
    onChange({
      publisherIds: selected
        ? state.publisherIds.filter((x) => x !== id)
        : [...state.publisherIds, id],
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-ink-muted">
        After the free week, ~€{SOURCE_MONTHLY_EUR} per source per month (€{SOURCE_MONTHLY_EUR} × N).
        {sourceCount > 0
          ? ` ${sourceCount} source${sourceCount === 1 ? "" : "s"} selected (~€${sourceCount * SOURCE_MONTHLY_EUR}/month).`
          : ""}
      </p>
      <input
        type="search"
        className="r1-field"
        placeholder="Search the source catalogue"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search sources"
      />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {results.map((source) => {
          const id = publisherKey(source);
          const selected = state.publisherIds.includes(id);
          return (
            <button
              key={id}
              type="button"
              className={cn(
                "flex flex-col items-center gap-2 rounded-xl border bg-white p-3 text-center",
                selected ? "border-brand-500 ring-1 ring-brand-500" : "border-slate-300 hover:border-brand-400",
              )}
              onClick={() => toggle(id)}
              aria-pressed={selected}
            >
              <img src={source.logoPath} alt="" width={160} height={80} className="h-10 w-auto" />
              <span className="text-sm font-semibold text-ink">{source.shortName}</span>
              <span className="text-xs text-ink-muted">{source.name}</span>
            </button>
          );
        })}
      </div>
      <div>
        <label className="text-sm font-medium text-ink-soft" htmlFor="request-source">
          Request a source
        </label>
        <input
          id="request-source"
          className="r1-field mt-1"
          placeholder="Name of regulator or industry body"
          value={state.requestedSource}
          onChange={(e) => onChange({ requestedSource: e.target.value })}
        />
        <p className="mt-1 text-xs text-ink-muted">{SOURCE_REQUEST_TURNAROUND}</p>
      </div>
    </div>
  );
}
